import React, { useRef } from 'react';
import cn from 'clsx';
import { CheckCircleIcon } from '@fronton/icons-react';
import { createElementKey, removeSpecialCharacters } from 'utils';
import { Button, Section, SectionSubTitle, Typography } from 'common/components';
import { fullWidthButton } from 'common/components/button/button-styles';
import { usePromoViewEvent } from 'common/hooks';
import { strictBreakpoints } from 'common/styles/breakpoints';
import { analyticsUtils } from 'utils/analytics-utils';
import { PROJECT_LIST } from './storage-projects-constants';
import {
  storageProject,
  storageProjectBenefit,
  storageProjectBenefits,
  storageProjectBtn,
  storageProjectImage,
  storageProjectName,
  storageProjects,
} from './storage-projects-styles';

export const StorageProjects: React.FC<{ elementBlock: string }> = ({ elementBlock }) => {
  const cardRefs = useRef<HTMLDivElement[]>([]);

  const promotions = PROJECT_LIST.map((project, index: number) => ({
    name: `${elementBlock}_top`,
    creative: removeSpecialCharacters(project.title),
    position: (index + 1).toString(),
  }));

  usePromoViewEvent(cardRefs, `${elementBlock} / stili`, promotions);

  const handleBtnClick = (index: number) => () => {
    analyticsUtils.promoClickEvent(elementBlock, promotions[index]);
  };

  const handleBtnWheelClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    if (e.button === 1) {
      handleBtnClick(index)();
    }
  };

  return (
    <Section className={storageProjects}>
      {PROJECT_LIST.map((project, index) => (
        <div
          data-position={index}
          ref={(el: HTMLDivElement | null) => {
            if (el && cardRefs) cardRefs.current[index] = el;
          }}
          className={storageProject}
          key={createElementKey('project', index)}
        >
          <SectionSubTitle className={storageProjectName} margin="s">
            {project.title}
          </SectionSubTitle>

          <picture>
            <source srcSet={project.img.srcLd} media={strictBreakpoints['--only-largeDesktop']} />
            <source srcSet={project.img.srcD} media={strictBreakpoints['--only-desktop']} />
            <source srcSet={project.img.srcT} media={strictBreakpoints['--only-tablet']} />
            <img alt={project.img.alt} className={storageProjectImage} src={project.img.src} />
          </picture>

          <div>
            <Typography size="body_short" variant="m">
              {project.description}
            </Typography>
            <ul className={storageProjectBenefits}>
              {project.benefits.map((benefit, benefitIndex) => (
                <li className={storageProjectBenefit} key={createElementKey('benefit', benefitIndex)}>
                  <CheckCircleIcon type='fill' size='l' color='control-accent' />
                  <Typography size="body_short" variant="s">{benefit}</Typography>
                </li>
              ))}
            </ul>

            <Button
              className={cn(fullWidthButton, storageProjectBtn)}
              onClick={handleBtnClick(index)}
              onAuxClick={(e) => handleBtnWheelClick(e, index)}
              href={project.button.href}
              title={project.button.title}
            >
              {project.button.label}
            </Button>
          </div>
        </div>
      ))}
    </Section>
  );
}